import { AlertCircle, Save, Trash2, ArrowLeft } from 'lucide-react';

/**
 * Modal som vises når brukeren prøver å forlate skjemaet med ulagrede
 * endringer (task #303, 2026-07-02). Tre valg:
 *  1. Lagre utkast — kaller handleSave('draft') og navigerer
 *  2. Forkast endringer — sletter cache og navigerer uten å lagre
 *  3. Fortsett redigering — lukker modalen, ingen navigasjon
 */

interface UnsavedChangesModalProps {
  /** true = brukeren står i eksisterende plan; false = ny plan uten DB-post */
  isExistingPlan: boolean;
  /** Kalles når bruker klikker "Lagre utkast" */
  onSaveAndLeave: () => void;
  /** Kalles når bruker klikker "Forkast endringer" */
  onDiscardAndLeave: () => void;
  /** Kalles når bruker klikker "Fortsett redigering" */
  onKeepEditing: () => void;
}

export default function UnsavedChangesModal({
  isExistingPlan,
  onSaveAndLeave,
  onDiscardAndLeave,
  onKeepEditing,
}: UnsavedChangesModalProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-lg w-full overflow-hidden">
        {/* Header */}
        <div className="bg-amber-50 border-b border-amber-200 p-4 flex items-start gap-3">
          <AlertCircle className="text-amber-600 flex-shrink-0 mt-0.5" size={22} />
          <div>
            <h2 className="text-lg font-bold text-amber-900">Ulagrede endringer</h2>
            <p className="text-sm text-amber-800 mt-1">
              {isExistingPlan
                ? 'Du har gjort endringer som ikke er lagret. Hva vil du gjøre før du forlater siden?'
                : 'Denne studieplanen er ikke lagret ennå. Hva vil du gjøre før du forlater siden?'}
            </p>
          </div>
        </div>

        {/* Choices */}
        <div className="p-4 space-y-3">
          <button
            type="button"
            onClick={onSaveAndLeave}
            className="w-full flex items-start gap-3 p-3 text-left border border-kf-blue-200 bg-kf-blue-50 rounded hover:bg-kf-blue-100 transition-colors"
          >
            <Save className="text-kf-blue-600 flex-shrink-0 mt-0.5" size={20} />
            <div>
              <div className="font-semibold text-kf-blue-900">Lagre {isExistingPlan ? 'endringene' : 'som utkast'}</div>
              <div className="text-sm text-kf-blue-800">
                {isExistingPlan
                  ? 'Endringene lagres som nytt utkast. Du kan fortsette senere.'
                  : 'Studieplanen opprettes som utkast slik at du kan fortsette senere.'}
              </div>
            </div>
          </button>

          <button
            type="button"
            onClick={onDiscardAndLeave}
            className="w-full flex items-start gap-3 p-3 text-left border border-red-200 bg-red-50 rounded hover:bg-red-100 transition-colors"
          >
            <Trash2 className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
            <div>
              <div className="font-semibold text-red-900">Forkast {isExistingPlan ? 'endringene' : 'utkastet'}</div>
              <div className="text-sm text-red-800">
                {isExistingPlan
                  ? 'Endringene siden siste lagring går tapt. Sist lagrede versjon beholdes.'
                  : 'Alt du har skrevet forsvinner. Ingen studieplan opprettes.'}
              </div>
            </div>
          </button>

          <button
            type="button"
            onClick={onKeepEditing}
            className="w-full flex items-start gap-3 p-3 text-left border border-gray-200 bg-white rounded hover:bg-gray-50 transition-colors"
          >
            <ArrowLeft className="text-gray-600 flex-shrink-0 mt-0.5" size={20} />
            <div>
              <div className="font-semibold text-gray-900">Fortsett redigering</div>
              <div className="text-sm text-gray-700">
                Lukk denne dialogen og gå tilbake til skjemaet.
              </div>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
}
